import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@clerk/clerk-react";
import { accessApi } from "@/lib/api/access";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface TopUpQuotaDialogProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userName?: string;
}

export function TopUpQuotaDialog({ userId, isOpen, onClose, onSuccess, userName }: TopUpQuotaDialogProps) {
  const [amount, setAmount] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setAmount("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    const quotaAmount = parseInt(amount);
    if (isNaN(quotaAmount) || quotaAmount <= 0) {
        toast.error("Invalid Amount", {
            description: "Please enter a positive number for the quota amount.",
        });
        return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("No authentication token available");

      await accessApi.addQuota(token, userId, quotaAmount);

      toast.success("Quota Added", {
        description: `Successfully added ${quotaAmount} quota to ${userName || "User"}.`,
      });

      onSuccess();
      onClose();
      setAmount("");

    } catch (error) {
      console.error("Failed to add quota:", error);
      toast.error("Error", {
        description: error instanceof Error ? error.message : "Failed to add quota",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Top Up Quota</DialogTitle>
          <DialogDescription>
            Add extra quota to {userName ? <b>{userName}</b> : "user"}. This will allow them to create more videos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount" className="text-right">
                Amount
              </Label>
              <Input
                id="amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="col-span-3"
                placeholder="e.g. 10"
                min="1"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Quota
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
